import axiosInstance from "../../utils/setupAxios";
import { FieldError, UseFormRegister, WatchInternal } from "react-hook-form";
import { setUserData } from "../../store/userSlice";
import { convertToEnglishNumbers, rawPhoneNumber } from "../../utils/generalUtils";
import { useTranslation } from "../../hooks/useTranslation";

export type PagesType = "signin" | "signup" | "forget" | "verify_signup" | "verify_forget" | "change_forget_password";

// TODO: spliting the signin/up into separate pages to improve UX and enable direct link sharing for each authentication flow
// TODO: the split process will simplify this type
export type SignPropsType = {
  register: UseFormRegister<{
    signin_phone: string;
    signin_password: string;
    signin_remember: boolean;
    signup_name: string;
    // TODO: re-enbale
    // signup_email: string;
    signup_phone: string;
    signup_password: string;
    signup_confirm_password: string;
    forget_phone: string;
    forget_password: string;
    forget_confirm_password: string;
    verify_code: string;
  }>;
  // onSocialLoginHandler?: CallableFunction;
  setActivePage?: CallableFunction;
  setValue?: CallableFunction;
  watch?: WatchInternal<string>;
  errors?: {
    signin_phone?: FieldError | undefined;
    signin_password?: FieldError | undefined;
    signin_remember?: FieldError | undefined;
    signup_name?: FieldError | undefined;
    signup_email?: FieldError | undefined;
    signup_phone?: FieldError | undefined;
    signup_password?: FieldError | undefined;
    signup_confirm_password?: FieldError | undefined;
    forget_phone?: FieldError | undefined;
    forget_password?: FieldError | undefined;
    forget_confirm_password?: FieldError | undefined;
    verify_code?: FieldError | undefined;
  };
  isSubmitting?: boolean;
  submitSuccess?: boolean;
  isSocialRegister?: boolean;
};

// TODO: move to fetcher.ts to unify the repo
export const sendVerificationMessage = async (phone: string, forget_password: boolean = false) => {
  return await axiosInstance.post("verify/send", {
    phone: phone,
    ...(forget_password ? { forget_password: true } : {}),
  });
};

export const updateUserPhone = async (phone: string, code: string) => {
  return await axiosInstance.patch("user/update-phone", {
    phone,
    code,
  });
};

export const changeUserPassword = async (old_password: string, new_password: string) => {
  return await axiosInstance.patch("user/change-password", {
    old_password,
    new_password,
  });
};

export const verifyUserPhone = async (phone: string, code: string) => {
  return await axiosInstance.post("verify", {
    phone: phone,
    code,
  });
};

export const registerUserWithEmail = async (
  name: string,
  phone: string,
  // TODO: remove the null when re-intergrating social login
  email: string | null,
  password: string,
  code: string,
) => {
  return await axiosInstance.post("auth/register", {
    name,
    phone: phone,
    email,
    password,
    code,
  });
};

// TODO: rename this function to signUserInWithPhone
export const signUserInWithEmail = async (phone: string, password: string) => {
  return await axiosInstance.post("auth/login", {
    phone: phone,
    password,
  });
};

export const getUserProfile = async () => {
  return await axiosInstance.get("user/profile");
};

export const changeForgottenPassword = async (phone: string, password: string, code: string) => {
  return await axiosInstance.patch("user/forget-password", {
    phone: phone,
    password,
    code,
  });
};

export const refreshUserToken = async (refresh_token: string) => {
  return await axiosInstance.post("auth/refresh_token", { refresh_token });
};

export const socialLogin = async (provider: "facebook" | "google", access_token: string) => {
  return await axiosInstance.post(`auth/${provider}`, { access_token });
};

// TODO: spliting the signin/up into separate pages to improve UX and enable direct link sharing for each authentication flow
// TODO: the split process will simplify this function
export const submitFormData = async (
  data: any,
  activePage: PagesType,
  setActivePage: CallableFunction,
  setSubmittingState: CallableFunction,
  dispatch: CallableFunction,
  locale: ReturnType<typeof useTranslation>["locale"],
  history: any,
) => {
  setSubmittingState({
    error: "",
    submitSuccess: false,
    isSubmitting: true,
  });
  let submitError: string = "",
    submitSuccess = false;
  if (activePage === "signup") {
    await sendVerificationMessage("+20" + rawPhoneNumber(convertToEnglishNumbers(data.signup_phone)))
      .then((res) => {
        if (res.status === 200) {
          setSubmittingState({ error: "", isSubmitting: false });
          setActivePage("verify_signup");
        }
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  } else if (activePage === "verify_signup") {
    await verifyUserPhone("+20" + rawPhoneNumber(convertToEnglishNumbers(data.signup_phone)), data.verify_code)
      .then(async (res) => {
        if (res.status === 200)
          await registerUserWithEmail(
            data.signup_name,
            "+20" + rawPhoneNumber(convertToEnglishNumbers(data.signup_phone)),
            // TODO: remove the null and enable `data.signup_email`
            null,
            // data.signup_email,
            data.signup_password,
            data.verify_code,
          )
            .then(async (res) => {
              if (res.status === 200) {
                localStorage.setItem(
                  "romotors_web_credentials",
                  JSON.stringify({
                    access_token: res.data.access_token,
                    expires_in: Date.now() + res.data.expires_in,
                    refresh_token: res.data.refresh_token,
                  }),
                );
                await getUserProfile()
                  .then((res) => {
                    dispatch(setUserData(res.data.profile));
                    history.replace("/");
                  })
                  .catch((error) => {
                    submitError = error?.response?.data?.message || locale("error_occured");
                  });
              }
            })
            .catch((error) => {
              submitError = error?.response?.data?.message || locale("error_occured");
            });
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  } else if (activePage === "signin") {
    await signUserInWithEmail("+20" + rawPhoneNumber(convertToEnglishNumbers(data.signin_phone)), data.signin_password)
      .then(async (res) => {
        if (res.status === 200) {
          localStorage.setItem(
            "romotors_web_credentials",
            JSON.stringify({
              access_token: res.data.access_token,
              expires_in: Date.now() + res.data.expires_in,
              refresh_token: res.data.refresh_token,
            }),
          );
          await getUserProfile()
            .then((res) => {
              dispatch(setUserData(res.data.profile));
              history.replace("/");
            })
            .catch((error) => {
              submitError = error?.response?.data?.message || locale("error_occured");
            });
        }
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  } else if (activePage === "forget") {
    await sendVerificationMessage("+20" + rawPhoneNumber(convertToEnglishNumbers(data.forget_phone)), true)
      .then((res) => {
        if (res.status === 200) {
          setSubmittingState({ error: "", isSubmitting: false });
          setActivePage("verify_forget");
        }
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  } else if (activePage === "verify_forget") {
    await verifyUserPhone("+20" + rawPhoneNumber(convertToEnglishNumbers(data.forget_phone)), data.verify_code)
      .then(async (res) => {
        if (res.status === 200) {
          setSubmittingState({ error: "", isSubmitting: false });
          setActivePage("change_forget_password");
        }
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  } else if (activePage === "change_forget_password") {
    await changeForgottenPassword(
      "+20" + rawPhoneNumber(convertToEnglishNumbers(data.forget_phone)),
      data.forget_password,
      data.verify_code,
    )
      .then((res) => {
        if (res.status === 200) {
          submitSuccess = true;
        }
      })
      .catch((error) => {
        submitError = error?.response?.data?.message || locale("error_occured");
      });
  }
  if (submitError)
    setSubmittingState({
      isSubmitting: false,
      error: submitError,
    });
  if (submitSuccess)
    setSubmittingState({
      isSubmitting: false,
      submitSuccess,
      error: submitError,
    });
};
